import type { ListLink } from "./listResults";
import { bffApi } from "../../autogen/bff-api";
import { fetchFresh } from "../redux/fetchFresh";
import type {
  AgentConversationDto,
  ListAgentConversationsApiResponse,
  UpdateAgentConversationApiArg,
  UpdateAgentConversationApiResponse,
} from "../../autogen/bff-api";
import type { AppDispatch } from "../redux/store";
import type { WireMessage } from "./agentMemory";
import { whatTheUserTyped } from "./aiResultHandoff";
import type { AgentTurn, EntityHighlight, ToolStep } from "./agentTypes";
import type { ConversationAttachment } from "./conversationFiles";
import type { AnsweredOn, ConversationSubject } from "./conversationSubject";
import { toResumableConversation } from "./resumableConversation";

export const CONVERSATION_TITLE_MAX_LENGTH = 40;
const FALLBACK_TITLE = "Ny samtale";

export type PersistedTurn = {
  user: string;
  steps: ToolStep[];
  note: string | null;
  answerText: string | null;
  entities?: EntityHighlight[];
  lists?: ListLink[];
  startButtonLabel?: string;
  failed?: boolean;
  changedSomething?: boolean;
  attachments?: ConversationAttachment[];
  answeredOn?: AnsweredOn;
};

export type ClientState = {
  turns: PersistedTurn[];
  entityIndex: Record<string, Record<string, unknown>>;
  model?: string;
  subject?: ConversationSubject;
};

export const toPersistedTurn = (turn: AgentTurn): PersistedTurn => ({
  user: turn.user,
  steps: turn.steps,
  note: turn.note,
  answerText: turn.answerText,
  ...(turn.entities ? { entities: turn.entities } : {}),
  ...(turn.lists?.length ? { lists: turn.lists } : {}),
  ...(turn.startButtonLabel ? { startButtonLabel: turn.startButtonLabel } : {}),
  ...(turn.failed ? { failed: true } : {}),
  ...(turn.changedSomething ? { changedSomething: true } : {}),
  ...(turn.attachments?.length ? { attachments: turn.attachments } : {}),
  ...(turn.answeredOn ? { answeredOn: turn.answeredOn } : {}),
});

export const toRestoredTurn = (turn: PersistedTurn): AgentTurn => ({
  user: turn.user,
  steps: turn.steps ?? [],
  note: turn.note ?? null,
  answerText: turn.answerText ?? null,
  entities: turn.entities,
  lists: turn.lists,
  startButtonLabel: turn.startButtonLabel,
  failed: turn.failed,
  changedSomething: turn.changedSomething,
  attachments: turn.attachments,
  answeredOn: turn.answeredOn,
});

export const buildClientState = (
  turns: AgentTurn[],
  entityIndex: Map<string, Record<string, unknown>>,
  model?: string,
  subject?: ConversationSubject | null,
): ClientState => ({
  turns: turns.map(toPersistedTurn),
  entityIndex: Object.fromEntries(entityIndex),
  ...(model ? { model } : {}),
  ...(subject ? { subject } : {}),
});

export const serializeClientState = (state: ClientState): string => JSON.stringify(state);

export const parseClientState = (raw: string): ClientState => {
  try {
    const parsed = JSON.parse(raw) as Partial<ClientState> | null;
    return {
      turns: parsed?.turns ?? [],
      entityIndex: parsed?.entityIndex ?? {},
      model: parsed?.model,
      ...(parsed?.subject ? { subject: parsed.subject } : {}),
    };
  } catch {
    return { turns: [], entityIndex: {} };
  }
};

export const restoreEntityIndex = (
  record: Record<string, Record<string, unknown>>,
): Map<string, Record<string, unknown>> => new Map(Object.entries(record));

export const firstUserContent = (messages: WireMessage[]): string | undefined => {
  const content = messages.find((m) => m.role === "user")?.content;
  return content ? whatTheUserTyped(content) : undefined;
};

export const deriveTitle = (firstUserMessage: string | null | undefined): string => {
  const trimmed = (firstUserMessage ?? "").trim();
  if (!trimmed) {
    return FALLBACK_TITLE;
  }
  return trimmed.length > CONVERSATION_TITLE_MAX_LENGTH
    ? trimmed.slice(0, CONVERSATION_TITLE_MAX_LENGTH).trimEnd()
    : trimmed;
};

export const getConversation = (dispatch: AppDispatch, conversationId: string): Promise<AgentConversationDto> =>
  fetchFresh(dispatch(bffApi.endpoints.getAgentConversation.initiate({ conversationId }, { forceRefetch: true })));

export const listOlderConversations = (
  dispatch: AppDispatch,
  before: string,
  limit: number,
): Promise<ListAgentConversationsApiResponse> =>
  fetchFresh(dispatch(bffApi.endpoints.listAgentConversations.initiate({ limit, before }, { forceRefetch: true })));

export const createConversation = (dispatch: AppDispatch, title: string): Promise<AgentConversationDto> =>
  dispatch(bffApi.endpoints.createAgentConversation.initiate({ createAgentConversationRequest: { title } })).unwrap();

export type ConversationUpdate = {
  title: string;
  messages: WireMessage[];
  clientState: string;
  procurementId?: string | null;
};

export const procurementOf = (subject: ConversationSubject | undefined): string | null =>
  subject?.kind === "v2Event" ? subject.id : null;

export const updateConversation = (
  dispatch: AppDispatch,
  conversationId: string,
  update: ConversationUpdate,
): Promise<AgentConversationDto> =>
  dispatch(
    bffApi.endpoints.updateAgentConversation.initiate({ conversationId, updateAgentConversationRequest: update }),
  ).unwrap();

const withoutListRefresh = bffApi.injectEndpoints({
  endpoints: (build) => ({
    updateAgentConversationWithoutListRefresh: build.mutation<
      UpdateAgentConversationApiResponse,
      UpdateAgentConversationApiArg
    >({
      query: (queryArg) => ({
        url: `/api/buyer/agent/conversations/${queryArg.conversationId}`,
        method: "PUT",
        body: queryArg.updateAgentConversationRequest,
      }),
    }),
  }),
});

export const updateConversationWithoutListRefresh = (
  dispatch: AppDispatch,
  conversationId: string,
  update: ConversationUpdate,
): Promise<AgentConversationDto> =>
  dispatch(
    withoutListRefresh.endpoints.updateAgentConversationWithoutListRefresh.initiate({
      conversationId,
      updateAgentConversationRequest: update,
    }),
  ).unwrap();

export const deleteConversation = (dispatch: AppDispatch, conversationId: string): Promise<unknown> =>
  dispatch(bffApi.endpoints.deleteAgentConversation.initiate({ conversationId })).unwrap();

export type ConversationSaveTriggers = {
  create: (title: string) => Promise<AgentConversationDto>;
  update: (conversationId: string, update: ConversationUpdate) => Promise<unknown>;
};

export type ConversationSaveInput = {
  activeConversationId: string | null;
  messages: WireMessage[];
  clientState: ClientState;
};

// The first save of a fresh conversation creates it (title = first user message) then updates it with
// the wire array + client state; every later save is a plain update. onCreated fires the moment the id
// exists so a failed update never re-creates a duplicate on the next retry.
export const saveConversation = async (
  triggers: ConversationSaveTriggers,
  input: ConversationSaveInput,
  onCreated?: (conversationId: string) => void,
): Promise<string> => {
  const firstTurn = input.clientState.turns[0];
  const onlyAFile = firstTurn && !firstTurn.user ? firstTurn.attachments?.[0]?.fileName : undefined;
  const title =
    firstTurn?.startButtonLabel ?? deriveTitle(onlyAFile ?? firstUserContent(input.messages) ?? firstTurn?.user);
  let conversationId = input.activeConversationId;
  if (conversationId === null) {
    conversationId = (await triggers.create(title)).id;
    onCreated?.(conversationId);
  }
  await triggers.update(conversationId, {
    title,
    messages: input.messages,
    clientState: serializeClientState(input.clientState),
    procurementId: procurementOf(input.clientState.subject),
  });
  return conversationId;
};

export const INTERRUPTED_NOTE = "Svaret ble avbrutt.";
export const INTERRUPTED_MARK = "[Svaret ble avbrutt her, så teksten over er bare delvis skrevet.]";
export const INTERRUPTED_BEFORE_ANY_TEXT = "[Svaret ble avbrutt før noe ble skrevet.]";

// Bedrock's Converse API refuses two user messages in a row, so the interrupted turn ends on a reply.
export const snapshotOfTurnInFlight = (
  messages: WireMessage[],
  clientState: ClientState,
): { messages: WireMessage[]; clientState: ClientState } => {
  const balanced = toResumableConversation(messages).messages;
  const turns = clientState.turns;
  const running = turns[turns.length - 1];
  if (!running) return { messages: balanced, clientState };
  const written = running.answerText?.trim();
  const last = balanced[balanced.length - 1];
  const endsOnAnswer = last?.role === "assistant" && !last.toolCalls?.length;
  const reply = written ? `${written}\n\n${INTERRUPTED_MARK}` : INTERRUPTED_BEFORE_ANY_TEXT;
  return {
    messages: endsOnAnswer ? balanced : [...balanced, { role: "assistant", content: reply }],
    clientState: {
      ...clientState,
      turns: [...turns.slice(0, -1), { ...running, note: running.note ?? INTERRUPTED_NOTE }],
    },
  };
};
